import { useCallback, useMemo, useState } from 'react'
import { tr } from './i18n'

/* Live form checks.
 *
 * Each field is checked as it is typed rather than when the form is sent, so
 * a mistake is pointed out while the cursor is still in it. A field that has
 * not been touched yet stays quiet: an empty form should not open covered in
 * red. Submitting marks every field touched, so anything skipped shows too. */

/** Returns a sentence when the value is wrong, or null when it is fine. */
export type Rule = (value: string) => string | null

/* The sentences are translated when a value is checked, in whatever language
   is chosen at that moment, so callers keep passing plain labels. */
export const rules = {
  required: (label: string): Rule => (v) => (v.trim() ? null : tr('desk.vRequired', { label })),

  name: (label?: string): Rule => (v) => {
    const t = v.trim()
    const l = label ?? tr('common.name')
    if (!t) return tr('desk.vRequired', { label: l })
    if (t.length < 2) return tr('desk.vTooShort', { label: l })
    if (t.length > 120) return tr('desk.vTooLong', { label: l })
    if (!/^[\p{L}\p{M}\s.'-]+$/u.test(t)) return tr('desk.vNameChars', { label: l })
    return null
  },

  /** Indian mobile: ten digits starting 6-9, with an optional +91 / 0 prefix. */
  mobile: (optional = false): Rule => (v) => {
    const t = v.trim()
    if (!t) return optional ? null : tr('desk.vMobileRequired')
    const digits = t.replace(/[\s-]/g, '')
    if (!/^\+?\d+$/.test(digits)) return tr('desk.vDigitsOnly')
    const local = digits.replace(/^(\+91|91|0)(?=\d{10}$)/, '')
    if (local.length !== 10) return tr('desk.vMobileDigits', { n: local.length })
    if (!/^[6-9]/.test(local)) return tr('desk.vMobileStart')
    return null
  },

  int: (label: string, min: number, max: number, optional = true): Rule => (v) => {
    const t = v.trim()
    if (!t) return optional ? null : tr('desk.vRequired', { label })
    if (!/^\d+$/.test(t)) return tr('desk.vWholeNumber', { label })
    const n = Number(t)
    if (n < min || n > max) return tr('desk.vBetween', { label, min, max })
    return null
  },

  money: (label: string, optional = true): Rule => (v) => {
    const t = v.trim()
    if (!t) return optional ? null : tr('desk.vRequired', { label })
    if (!/^\d+(\.\d{1,2})?$/.test(t)) return tr('desk.vAmount', { label })
    return null
  },

  /**
   * The sign-in box: a phone number or an email, and (unless `phoneOrEmail`)
   * an older account's username. Same rules as server/src/services/identity.ts.
   */
  identifier: (phoneOrEmail = false): Rule => (v) => {
    const t = v.trim()
    if (!t) return tr('access.vIdentifier')
    if (t.includes('@')) return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(t) ? null : tr('access.vEmail')
    if (/^\+?[\d\s-]+$/.test(t)) {
      const local = t.replace(/[\s-]/g, '').replace(/^(\+91|91|0)(?=\d{10}$)/, '')
      return /^[6-9]\d{9}$/.test(local) ? null : tr('access.vPhone')
    }
    if (phoneOrEmail) return tr('access.vPhoneOrEmail')
    return /^[a-z0-9._-]{3,64}$/i.test(t) ? null : tr('access.vIdentifierShape')
  },

  /** A new password: the server's rule (8-128 characters, not only digits). */
  password: (): Rule => (v) => {
    if (v.length < 8) return tr('access.vPasswordShort')
    if (v.length > 128) return tr('access.vPasswordLong')
    if (/^\d+$/.test(v)) return tr('access.vPasswordDigits')
    return null
  },

  /** Diabetic or Non-Diabetic, one of the two and nothing else. */
  category: (): Rule => (v) => (v === 'diabetes' || v === 'non_diabetic' ? null : tr('desk.vCategory')),

  pincode: (): Rule => (v) => {
    const t = v.trim()
    if (!t) return null
    return /^[1-9]\d{5}$/.test(t) ? null : tr('desk.vPincode')
  },

  email: (): Rule => (v) => {
    const t = v.trim()
    if (!t) return null
    // Same shape the server accepts (server/src/services/identity.ts), so a form
    // never passes here and then fails there.
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(t) && t.length <= 254 ? null : tr('desk.vEmail')
  },

  maxLen: (label: string, max: number): Rule => (v) =>
    (v.trim().length > max ? tr('desk.vTooLongCount', { label, n: v.trim().length, max }) : null),

  /** HH:MM after another HH:MM. */
  after: (other: () => string, label?: string): Rule => (v) =>
    (v && other() && v <= other() ? tr('desk.vAfterStart', { label: label ?? tr('desk.vEnd') }) : null),

  notPast: (label?: string): Rule => (v) => {
    const l = label ?? tr('common.date')
    if (!v) return tr('desk.vRequired', { label: l })
    return new Date(v).getTime() < Date.now() - 60_000 ? tr('desk.vNotPast', { label: l }) : null
  },
}

/**
 * What a patient's mobile box keeps: digits only, ten at most. A +91 or 0 in
 * front of a full number is dropped rather than the end cut off, so a number
 * carried in from the search box or an older record keeps its ten digits.
 * The box itself has no maxLength: the browser would cut a pasted
 * '+91 98765 43210' or '98765 43210' to ten characters before this saw it.
 */
export function mobileDigits(v: string): string {
  return v.replace(/\D/g, '').replace(/^(91|0)(?=\d{10}$)/, '').slice(0, 10)
}

/** Runs several rules in order and keeps the first complaint. */
export function all(...list: Rule[]): Rule {
  return (v) => {
    for (const r of list) {
      const e = r(v)
      if (e) return e
    }
    return null
  }
}

/**
 * Validation state for one form.
 *
 * `values` is whatever the form already keeps; this hook never owns it, so it
 * can be dropped into an existing screen without reshaping its state.
 */
export function useLiveValidation<K extends string>(
  values: Record<K, string | number | boolean | null | undefined>,
  schema: Partial<Record<K, Rule>>,
) {
  const [touched, setTouched] = useState<Partial<Record<K, boolean>>>({})

  const errors = useMemo(() => {
    const out: Partial<Record<K, string>> = {}
    for (const key of Object.keys(schema) as K[]) {
      const rule = schema[key]
      if (!rule) continue
      const e = rule(String(values[key] ?? ''))
      if (e) out[key] = e
    }
    return out
    // The schema is usually an inline object; the values are what change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  /** Mark a field as typed into. Call from onChange (every keystroke). */
  const touch = useCallback((key: K) => {
    setTouched((t) => (t[key] ? t : { ...t, [key]: true }))
  }, [])

  /** Mark everything, e.g. on submit, so skipped fields show their complaint. */
  const touchAll = useCallback(() => {
    setTouched(Object.fromEntries(Object.keys(schema).map((k) => [k, true])) as Partial<Record<K, boolean>>)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reset = useCallback(() => setTouched({}), [])

  /** The message to show under a field, only once it has been touched. */
  const errorOf = (key: K): string | null => (touched[key] ? errors[key] ?? null : null)

  const valid = Object.keys(errors).length === 0

  return { errors, errorOf, touch, touchAll, reset, valid }
}
